import type { TimelineItem } from '@/components/detail/DetailTimeline';

export interface DealTimelineMeeting {
  id: number;
  title: string;
  startTime: string;
  endTime?: string | null;
  meetingType?: string | null;
}

function parsedActivityMetadata(activity: any): any {
  if (typeof activity?.metadata !== 'string') return activity?.metadata;
  try {
    return JSON.parse(activity.metadata);
  } catch {
    return null;
  }
}

export function activityMeetingId(activity: any): number | null {
  const candidates = [
    activity?.meetingId,
    activity?.meeting_id,
    parsedActivityMetadata(activity)?.meetingId,
    parsedActivityMetadata(activity)?.meeting_id,
  ];
  for (const candidate of candidates) {
    const id = Number(candidate);
    if (Number.isSafeInteger(id) && id > 0) return id;
  }
  return null;
}

/** Solo un ID esplicito prova che Attivita e Meeting sono lo stesso evento. */
export function isActivityBackedByCanonicalMeeting(
  activity: any,
  meetings: Array<Pick<DealTimelineMeeting, 'id'>>,
): boolean {
  const meetingId = activityMeetingId(activity);
  return meetingId !== null && meetings.some((meeting) => Number(meeting.id) === meetingId);
}

export function formatDealMeetingRange(meeting: Pick<DealTimelineMeeting, 'startTime' | 'endTime'>): string {
  const start = new Date(meeting.startTime);
  if (Number.isNaN(start.getTime())) return 'Data da verificare';
  const date = new Intl.DateTimeFormat('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Rome',
  }).format(start);
  const startTimeText = new Intl.DateTimeFormat('it-IT', {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Rome',
  }).format(start);
  const end = meeting.endTime ? new Date(meeting.endTime) : null;
  const endTimeText = end && !Number.isNaN(end.getTime())
    ? new Intl.DateTimeFormat('it-IT', {
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Rome',
      }).format(end)
    : null;
  return `${date}, ${startTimeText}${endTimeText ? `–${endTimeText}` : ''}`;
}

export function meetingToDealTimelineItem(meeting: DealTimelineMeeting): TimelineItem {
  const range = formatDealMeetingRange(meeting);
  return {
    id: `meeting-${meeting.id}`,
    type: 'meeting',
    title: meeting.title,
    description: [meeting.meetingType, range].filter(Boolean).join(' · '),
    date: meeting.startTime,
    link: `/meetings/${meeting.id}`,
    linkLabel: 'Apri appuntamento',
  };
}
